<template>
  <BaseModal
    :is-open="isOpen"
    :show-default-footer="false"
    :close-on-overlay-click="true"
    :close-on-escape="true"
    :showCloseButton="true"
    content-padding="none"
    size="sm"
    title="비밀번호 오류 횟수 초과"
    @close="handleClose"
  >
    <!-- 모의모드 -->
    <div
      v-if="isDemoMode()"
      class="gap-size-16 py-size-40 px-size-50 flex flex-col items-center justify-center"
    >
      <img :src="exampleLoginImage" alt="login" />
      <p class="text-font-16 text-center font-medium">비밀번호 오류 횟수를 초과했어요</p>
      <p class="text-font-14 text-center">고객센터로 문의해주세요</p>
      <BaseButton
        label="확인"
        variant="contained"
        color="primary"
        size="md"
        full-width
        @click="handleClose"
      />
    </div>

    <!-- 일반 모드: 기존 UI -->
    <div v-else class="gap-size-36 py-size-40 px-size-50 flex flex-col">
      <div class="gap-size-12 flex flex-col items-center justify-center">
        <img :src="exampleLoginImage" alt="login" />

        <p class="text-font-16 font-medium">비밀번호 오류 횟수를 초과했어요</p>
        <p class="text-font-14">본인인증 후 비밀번호를 재설정 해주세요</p>
      </div>
      <div class="gap-size-10 flex w-full items-center justify-center">
        <BaseButton
          label="확인"
          variant="contained"
          color="primary"
          size="md"
          @click="handleVerifyPhone"
        />
        <BaseButton label="취소" variant="outlined" color="black" size="md" @click="handleClose" />
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { LocalStorageService, LocalStorageKey, isDemoMode } from '@template/utils';
import { useGlobalDialogManager } from '@/composables/useGlobalDialogManager';
import exampleLoginImage from '@/assets/image/dialog/exclamation-mark.png';
import type { KcbPhoneVerifyResultRequestMemberType } from '@template/api';
import { usePhoneVerification } from '@/composables/usePhoneVerification';
import { useToastStore } from '@/stores/useToastStore';
import { BaseModal, BaseButton } from '@template/ui';
import { useAccountStore } from '@template/stores';
import { accountService } from '@/services/api';
import { memberService } from '@/services/api';

const toastStore = useToastStore();
const globalDialogManager = useGlobalDialogManager();
const accountStore = useAccountStore();

const isOpen = defineModel<boolean>('isOpen', { required: true, default: false });

const { verifyPhone } = usePhoneVerification({
  onSuccess: async (result) => {
    try {
      globalDialogManager.closeAll();
      const response = await memberService.getInfo();
      if (response?.data?.phoneNo !== result.phoneNo) {
        toastStore.addToast(
          '가입 정보와 본인인증 정보가 일치하지 않아요 입력한 정보를 다시 확인해주세요.'
        );
      } else {
        globalDialogManager.open('accountPasswordResetInput', async (payload) => {
          try {
            const targetAccountNo =
              accountStore.resetChangeAccountNo || accountStore.selectedAccountNoSafe;

            await accountService.resetPassword({
              ci: result.ci,
              accountNo: targetAccountNo,
              afterAccountPassword: payload.password,
            });
            accountStore.resetAccountPasswordFailedCount();
            accountStore.clearResetChangeAccountNo();
            globalDialogManager.open('accountPasswordResetComplete');
          } catch (error) {
            console.error('계좌비밀번호 재설정 중 오류:', error);
          }
        });
      }
    } catch (error) {
      console.error('휴대폰 변경 본인인증 처리 중 오류:', error);
    }
  },
  onError: (error) => {
    console.error('휴대폰 변경 본인인증 처리 중 오류:', error);
  },
});

const handleClose = () => {
  isOpen.value = false;
  accountStore.clearResetChangeAccountNo();
};

const handleVerifyPhone = () => {
  const memberType = LocalStorageService.getItem(LocalStorageKey.MEMBER_TYPE);
  const verifiedMemberType: KcbPhoneVerifyResultRequestMemberType =
    memberType === 'INDIVIDUAL' || memberType === 'CORPORATE' ? memberType : 'INDIVIDUAL';
  verifyPhone('02', verifiedMemberType);
};
</script>
